import React, { useState } from 'react';
import { Shield, ChevronDown, ChevronRight, FileText, Calendar } from 'lucide-react';

interface AdminLicensesTabProps {
  licenses: any[];
}

const statusColors: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-red-100 text-red-700',
  'pending-renewal': 'bg-amber-100 text-amber-700',
};

const AdminLicensesTab: React.FC<AdminLicensesTabProps> = ({ licenses }) => {
  const [expandedLicenseId, setExpandedLicenseId] = useState<string | null>(null);

  if (licenses.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="text-center py-8 text-stone-400">
          <Shield size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No licenses recorded for this location</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {licenses.map((license: any) => {
        const isExpanded = expandedLicenseId === license.licenseId;
        const records = license.records || [];

        return (
          <div key={license.licenseId} className="bg-white rounded-2xl shadow-sm border border-stone-200/60 overflow-hidden">
            <button
              onClick={() => setExpandedLicenseId(isExpanded ? null : license.licenseId)}
              className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-stone-50 transition-colors"
            >
              <div className="w-9 h-9 bg-rose-50 rounded-lg flex items-center justify-center shrink-0">
                <Shield size={16} className="text-rose-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-stone-800 truncate">{license.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {license.issuingAuthority && <span className="text-xs text-stone-500">{license.issuingAuthority}</span>}
                  {license.licenseNumber && <span className="text-xs text-stone-400 font-mono">#{license.licenseNumber}</span>}
                </div>
              </div>
              <span className="text-xs text-stone-400 mr-2">{records.length} record{records.length !== 1 ? 's' : ''}</span>
              {license.status && (
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${statusColors[license.status] || 'bg-stone-100 text-stone-500'}`}>
                  {license.status}
                </span>
              )}
              <div className="shrink-0 text-stone-400">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-stone-100 px-5 py-4 bg-stone-50/50">
                {records.length === 0 ? (
                  <p className="text-sm text-stone-400 text-center py-4">No yearly records</p>
                ) : (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Yearly Records</h4>
                    {records.map((record: any, idx: number) => (
                      <div key={record.recordKey || idx} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-stone-100">
                        <div className="w-8 h-8 bg-stone-50 rounded-lg flex items-center justify-center shrink-0">
                          <Calendar size={14} className="text-stone-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-stone-700">{record.year}</p>
                          <div className="flex items-center gap-2 mt-0.5 text-xs text-stone-500">
                            {record.issueDate && <span>Issued: {record.issueDate}</span>}
                            {record.expiryDate && <span>Expires: {record.expiryDate}</span>}
                            {record.cost != null && <span>Cost: ${Number(record.cost).toLocaleString()}</span>}
                          </div>
                        </div>
                        {record.documentUrl && (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                            <FileText size={12} />
                            Document
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AdminLicensesTab;
